// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import { CartWidget } from '@gandi/cart';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { ShopLayout } from '../Layout';
import type { BreadCrumbProps } from '../Layout/components/Breadcrumb/Breadcrumb';
import { usePreferences } from '../Layout/hooks';
import { setPreferencesDialogOpen } from '../modules/app/actions';
import { selectPreferencesDialogOpen } from '../modules/app/selectors/layout';
import { selectAdminURL } from '../modules/config';
import type { FullState, PreFetchArgs, PreFetchReturn } from '../types';

import LayoutContext from './LayoutContext';
import {
  selectIsCartOverviewPage,
  selectIsCheckoutPage,
} from './modules/selectors';

import styles from './Layout.scss';

type Props = {
  children: React.Node,
  lockedOrga?: boolean,
  showCartSummary?: boolean,
  backgroundColor?: 'surface',
  /**
   * 1. Address
   * 2. Summary
   * 3. Payment
   * 4. Payment failed
   * 5. Confirmation (payment return success) – no breadcrumbs or menu displayed
   */
  currentStep?: BreadCrumbProps['currentStep'],
};

export function Layout({
  backgroundColor,
  showCartSummary,
  children,
  lockedOrga,
  currentStep,
}: Props): React.Node {
  const location = useLocation();
  const dashboardURL = useAppSelector(selectAdminURL);

  const open = useAppSelector(selectPreferencesDialogOpen);
  const dispatch = useDispatch();

  const preferences = usePreferences();

  const preferencesDialog = {
    open,
    close: () => dispatch(setPreferencesDialogOpen(false)),
    show: () => dispatch(setPreferencesDialogOpen(true)),
  };

  const contextValue = React.useMemo(
    () => ({ locked: !!lockedOrga }),
    [lockedOrga],
  );

  const isCartOverviewPage = selectIsCartOverviewPage(location);

  const isCheckoutPage = selectIsCheckoutPage(location);

  return (
    <LayoutContext.Provider value={contextValue}>
      <ShopLayout
        backgroundColor={backgroundColor}
        showCartSummary={showCartSummary}
        widgets={
          <div className={styles.toggle}>
            <CartWidget
              noSuggestions={isCartOverviewPage}
              noCheckoutButton={isCheckoutPage}
              bypassRetrieveCart={isCheckoutPage}
              // $FlowFixMe[incompatible-type]
              openPreferences={preferencesDialog.show}
              preferences={preferences}
            />
          </div>
        }
        dashboardURL={dashboardURL}
        // $FlowFixMe[incompatible-type]
        preferencesDialog={preferencesDialog}
        currentStep={currentStep}
      >
        {children}
      </ShopLayout>
    </LayoutContext.Provider>
  );
}

Layout.preFetch = function preFetch(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  return ShopLayout.preFetch(args);
};

Layout.handleRedirect = function handleRedirect(
  args: StaticHandlerArgs<>,
): Promise<void> | Promise<mixed> {
  return ShopLayout.handleRedirect(args);
};
